import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Breadcrumbs } from "../components/breadcrumbs";
import { GUIDES } from "../data/guides";
import { InlineQuoteSection } from "@/components/inline-quote-section";

const SITE = "https://capitalpropressurewashing.com";
const TITLE = "Guides | Capital Pro Pressure Washing";
const DESC =
  "Helpful guides on roof streaking, soft washing vs pressure washing, siding cleaning, and cleaning frequency for Capital Region, NY homes.";

export const Route = createFileRoute("/guides/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/guides` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: `${SITE}/guides` }],
  }),
  component: GuidesIndexPage,
});

function GuidesIndexPage() {
  return (
    <main>
      <Breadcrumbs items={[{ label: "Guides" }]} />

      <section className="bg-[var(--brand-deep-blue)] text-white py-14 md:py-20">
        <div className="mx-auto max-w-[1200px] px-4 text-center">
          <p
            className="font-semibold uppercase tracking-[0.2em] text-sm mb-3"
            style={{ fontFamily: "var(--font-display)", color: "#ff6b7f" }}
          >
            Learn
          </p>
          <h1
            className="text-3xl md:text-5xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Pressure Washing Guides
          </h1>
          <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Real answers to the questions Capital Region homeowners ask us most — from roof
            streaking to how often you actually need to clean.
          </p>
        </div>
      </section>

      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[900px] px-4 grid grid-cols-1 md:grid-cols-2 gap-6">
          {GUIDES.map((g) => (
            <Link
              key={g.slug}
              to="/guides/$slug"
              params={{ slug: g.slug }}
              className="group flex flex-col rounded-lg border border-gray-200 overflow-hidden hover:border-[var(--brand-deep-blue)] transition-colors bg-[var(--brand-light-gray)]"
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={g.heroImage}
                  alt={g.title}
                  className="h-full w-full object-cover group-hover:scale-105 transition-transform"
                  loading="lazy"
                />
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h2
                  className="text-[var(--brand-deep-blue)] text-lg leading-tight mb-2"
                  style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                >
                  {g.title}
                </h2>
                <p className="text-[var(--brand-charcoal)]/80 text-sm leading-relaxed flex-1">
                  {g.description}
                </p>
                <span className="mt-4 inline-flex items-center gap-1 text-[var(--brand-red)] font-semibold text-sm uppercase tracking-wide">
                  Read Guide <ArrowRight size={16} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <InlineQuoteSection source="Guides Hub" />
    </main>
  );
}
